/**
 * Canonical permission catalogue.
 *
 * Every project-level capability the platform exposes is named exactly once
 * here. The `PERMISSIONS` const-object is the single source of truth: every
 * other RBAC artefact (Permissions interface, role-dialog grouping, dependency
 * graph, ACCESS_RESOURCES wiring, full-permission seed, route guards, nav data)
 * derives from these keys.
 *
 * Naming convention: `domain:action` or `domain:resource:action`.
 *
 * Permission shape per key:
 *  - `List-based` keys are filtered grants (AllOrSpecific<...>) and gate access
 *    to a specific subset of entities the role can see/act on.
 *  - `Boolean` keys are unconditional grants for an action that has no
 *    per-entity scoping at the permission layer.
 *
 * Org-level concerns (organizations, billing, org owners) are intentionally
 * out of scope.
 */

export const PERMISSIONS = {
	// Device Management
	// List-based permission. Controls which devices a role can view.
	DEVICE_READ: 'device:read',

	// Boolean permission. Allows provisioning devices at project level.
	DEVICE_PROVISION: 'device:provision',

	// Boolean permission. Allows activate/deactivate devices at project level.
	DEVICE_UPDATE: 'device:update',

	// List-based permission. Controls which device metadata key/value pairs a
	// role can view. Should be evaluated together with METADATA_READ for
	// metadata-key visibility.
	DEVICE_METADATA_READ: 'device-metadata:read',

	// List-based permission. Must be a subset of DEVICE_METADATA_READ.
	// Controls which visible metadata fields/values can be updated.
	DEVICE_METADATA_UPDATE: 'device-metadata:update',

	// Stream / Telemetry
	// List-based permission. Controls which streams and fields a role can view.
	// Encodes both stream-level and field-level read grants: the value is a
	// HashMap<StreamName, HashSet<FieldName>> that the backend evaluates exclusively.
	STREAM_READ: 'stream:read',

	// Boolean permission. Allows creating stream definitions.
	STREAM_CREATE: 'stream:create',

	// List-based permission. Must be a subset of STREAM_READ.
	// Allows modifying allowed streams, including CSV upload-like changes if
	// backend supports them.
	STREAM_UPDATE: 'stream:update',

	// List-based permission. Must be a subset of STREAM_READ.
	// Controls which streams a role can delete.
	STREAM_DELETE: 'stream:delete',

	// List-based permission. Must be a subset of STREAM_READ.
	// Allows editing only fields the role can already view.
	STREAM_FIELD_UPDATE: 'stream-field:update',

	// List-based permission. Must be a subset of STREAM_READ.
	// Allows deleting only fields the role can already view.
	STREAM_FIELD_DELETE: 'stream-field:delete',

	// Device State Fields
	// List-based permission. Controls which device-state fields a role can view.
	DEVICE_STATE_FIELD_READ: 'device-state-field:read',

	// List-based permission. Must be a subset of DEVICE_STATE_FIELD_READ.
	// Allows renaming only device-state fields the role can already view.
	DEVICE_STATE_FIELD_UPDATE: 'device-state-field:update',

	// List-based permission. Must be a subset of DEVICE_STATE_FIELD_READ.
	// Allows removing only device-state fields the role can already view.
	DEVICE_STATE_FIELD_DELETE: 'device-state-field:delete',

	// Boolean permission. Allows querying ClickHouse through approved
	// telemetry query APIs. Returned data must still be restricted by STREAM_READ.
	TELEMETRY_QUERY: 'telemetry:query',

	// Metadata
	// List-based permission. Allows viewing project metadata definitions/keys.
	METADATA_READ: 'metadata:read',

	// Boolean permission. Allows creating metadata definitions/keys.
	METADATA_CREATE: 'metadata:create',

	// Boolean permission. Allows updating metadata definitions/keys with
	// METADATA_READ permission.
	METADATA_UPDATE: 'metadata:update',

	// Boolean permission. Allows deleting metadata definitions/keys with
	// METADATA_READ permission.
	METADATA_DELETE: 'metadata:delete',

	// Dashboards
	// Boolean permission. Allows creating dashboards.
	DASHBOARD_CREATE: 'dashboard:create',
	// List-based permission. Controls which dashboards a role can view.
	DASHBOARD_READ: 'dashboard:read',
	// List-based permission. Controls which dashboards a role can edit/update.
	DASHBOARD_UPDATE: 'dashboard:update',
	// List-based permission. Controls which dashboards a role can delete.
	DASHBOARD_DELETE: 'dashboard:delete',

	// DBC Parsers
	// Boolean permission. Allows creating DBC parsers.
	DBC_PARSER_CREATE: 'dbc-parser:create',

	// List-based permission. Controls which DBC parsers a role can view.
	DBC_PARSER_READ: 'dbc-parser:read',

	// List-based permission. Must be a subset of DBC_PARSER_READ.
	// Controls which DBC parsers a role can update/start/stop.
	DBC_PARSER_UPDATE: 'dbc-parser:update',

	// List-based permission. Must be a subset of DBC_PARSER_READ.
	DBC_PARSER_DELETE: 'dbc-parser:delete',

	// List-based permission. Must be a subset of DBC_PARSER_READ.
	DBC_PARSER_FILE_DOWNLOAD: 'dbc-parser:download',

	// List-based permission. Must be a subset of DBC_PARSER_READ.
	// Controls which DBC parsers a role can reconfigure (re-upload DBC file).
	DBC_PARSER_RECONFIGURE: 'dbc-parser:reconfigure',

	// Exporters
	// List-based permission. Controls which exporters a role can view.
	EXPORTER_READ: 'exporter:read',

	// Boolean permission. Allows creating exporters.
	EXPORTER_CREATE: 'exporter:create',

	// List-based permission. Must be a subset of EXPORTER_READ.
	// Controls which exporters a role can update/start/stop.
	EXPORTER_UPDATE: 'exporter:update',

	// List-based permission. Must be a subset of EXPORTER_READ.
	EXPORTER_DELETE: 'exporter:delete',

	// Action Types / Commands / Shell
	// Boolean permission. Allows creating action types.
	ACTION_TYPE_CREATE: 'action-type:create',

	// List-based permission. Controls which action types a role can view.
	// Commands and shell are treated as action types.
	ACTION_TYPE_READ: 'action-type:read',

	// List-based permission. Must be a subset of ACTION_TYPE_READ.
	// Controls which action types a role can update/activate/deactivate.
	ACTION_TYPE_UPDATE: 'action-type:update',

	// List-based permission. Must be a subset of ACTION_TYPE_READ.
	ACTION_TYPE_DELETE: 'action-type:delete',

	// List-based permission. Must be a subset of ACTION_TYPE_READ.
	// Covers executing commands; also marking the action as completed.
	ACTION_TYPE_EXECUTE: 'action-type:execute',

	// Firmware
	// List-based permission. Controls which firmware artifacts a role can view.
	FIRMWARE_READ: 'firmware:read',

	// Boolean permission. Allows uploading/creating firmware artifacts.
	FIRMWARE_CREATE: 'firmware:create',

	// List-based permission. Must be a subset of FIRMWARE_READ.
	FIRMWARE_UPDATE: 'firmware:update',

	// List-based permission. Must be a subset of FIRMWARE_READ.
	FIRMWARE_DELETE: 'firmware:delete',

	// List-based permission. Must be a subset of FIRMWARE_READ.
	FIRMWARE_DOWNLOAD: 'firmware:download',

	// Files
	// List-based permission. Controls which file assets a role can view.
	FILES_READ: 'files:read',

	// Boolean permission. Allows uploading/creating file assets.
	FILES_CREATE: 'files:create',

	// List-based permission. Must be a subset of FILES_READ.
	FILES_UPDATE: 'files:update',

	// List-based permission. Must be a subset of FILES_READ.
	FILES_DELETE: 'files:delete',

	// List-based permission. Must be a subset of FILES_READ.
	FILES_DOWNLOAD: 'files:download',

	// Configuration (JSON + Geofence)
	// One permission family governs both JSON and geofence configuration assets.
	// List-based permission. Controls which configurations a role can view.
	CONFIG_READ: 'config:read',

	// Boolean permission. Allows creating configurations.
	CONFIG_CREATE: 'config:create',

	// List-based permission. Must be a subset of CONFIG_READ.
	CONFIG_UPDATE: 'config:update',

	// List-based permission. Must be a subset of CONFIG_READ.
	CONFIG_DELETE: 'config:delete',

	// Notifications
	// Boolean permission. Allows viewing notifications.
	NOTIFICATION_READ: 'notification:read',

	// Boolean permission. Allows creating notifications.
	NOTIFICATION_CREATE: 'notification:create',

	// Boolean permission. Allows updating notifications.
	// Controls whether a role can update/enable/disable.
	NOTIFICATION_UPDATE: 'notification:update',

	// Boolean permission. Allows deleting notifications.
	NOTIFICATION_DELETE: 'notification:delete',

	// Subscriber Groups
	// List-based permission. Controls which subscriber groups a role can view.
	SUBSCRIBER_GROUP_READ: 'subscriber-group:read',

	// Boolean permission. Allows creating subscriber groups.
	SUBSCRIBER_GROUP_CREATE: 'subscriber-group:create',

	// List-based permission. Must be a subset of SUBSCRIBER_GROUP_READ.
	// Controls which subscriber groups a role can update/enable/disable.
	SUBSCRIBER_GROUP_UPDATE: 'subscriber-group:update',

	// List-based permission. Must be a subset of SUBSCRIBER_GROUP_READ.
	SUBSCRIBER_GROUP_DELETE: 'subscriber-group:delete',

	// Session Rules
	// Boolean permission. Allows creating session rules.
	SESSION_RULE_CREATE: 'session-rule:create',

	// List-based permission. Controls which session rules a role can view.
	SESSION_RULE_READ: 'session-rule:read',

	// List-based permission. Must be a subset of SESSION_RULE_READ.
	// Controls which session rules a role can update/start/stop.
	SESSION_RULE_UPDATE: 'session-rule:update',

	// List-based permission. Must be a subset of SESSION_RULE_READ.
	SESSION_RULE_DELETE: 'session-rule:delete',

	// Roles
	// Boolean permission. Allows viewing project roles and permission matrix.
	ROLE_READ: 'role:read',

	// Boolean permission. Allows creating project roles.
	ROLE_CREATE: 'role:create',

	// Boolean permission. Allows updating project roles.
	ROLE_UPDATE: 'role:update',

	// Boolean permission. Allows deleting project roles.
	ROLE_DELETE: 'role:delete',

	// Users
	// Boolean permission. Allows viewing project users.
	USER_READ: 'user:read',

	// Boolean permission. Allows adding/creating a user membership if backend
	// supports direct add.
	USER_CREATE: 'user:create',

	// Boolean permission. Allows updating project user role/status.
	USER_UPDATE: 'user:update',

	// Boolean permission. Allows removing a user from the project.
	USER_DELETE: 'user:delete',

	// API Keys / Service Accounts
	// Boolean permission. Allows viewing API keys/service accounts.
	API_KEY_READ: 'api-key:read',

	// Boolean permission. Allows creating API keys/service accounts.
	API_KEY_CREATE: 'api-key:create',

	// Boolean permission. Allows updating API key name/role/status.
	API_KEY_UPDATE: 'api-key:update',

	// Boolean permission. Allows deleting/deactivating API keys/service accounts.
	API_KEY_DELETE: 'api-key:delete',

	// Project Settings
	// Boolean permission. Allows viewing project settings.
	PROJECT_SETTINGS_READ: 'project-settings:read',

	// Boolean permission. Allows updating project settings.
	PROJECT_SETTINGS_UPDATE: 'project-settings:update',
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]
